import { cn } from '@/lib/utils';
import { Lightbulb, ArrowRight } from 'lucide-react';

type ReflectivePrompt = {
  prompt: string;
  why_it_matters: string;
  micro_action: string;
};

type ReflectivePromptsProps = {
  title: string;
  data: ReflectivePrompt[];
  className?: string;
};

export default function ReflectivePrompts({ title, data, className }: ReflectivePromptsProps) {
  if (!data?.length) {
    return null;
  }

  return (
    <section
      className={cn(
        'rounded-2xl border border-primary/20 bg-primary/5 p-6 shadow-sm backdrop-blur-sm',
        className,
      )}
    >
      <header className="mb-4 flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
          <Lightbulb className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-200">{title}</h3>
          <p className="text-sm text-primary-700/80 dark:text-primary-100/80">
            Two quick nudges drawn from your recent patterns—use them to deepen reflection before your next move.
          </p>
        </div>
      </header>

      <ol className="space-y-4">
        {data.slice(0, 2).map((item, index) => (
          <li
            key={`${item.prompt}-${index}`}
            className="rounded-xl border border-primary/10 bg-white/70 p-4 shadow-sm transition hover:border-primary/40 dark:bg-slate-900/40"
          >
            <div className="flex items-start gap-3">
              <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {index + 1}
              </span>
              <div className="space-y-3">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.prompt}</p>
                <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200">
                  <p>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">Why this matters:</span>{' '}
                    {item.why_it_matters}
                  </p>
                  <p className="flex items-start gap-2 text-primary-700 dark:text-primary-200">
                    <ArrowRight className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{item.micro_action}</span>
                  </p>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
